import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useQuery } from '@apollo/client'

import { IFolderModel } from 'models/folder.model'
import * as fromActions from 'store/actions/folders.action'
import SelectInput from 'components/ui/forms/field/select-input/SelectInput'
import {
  COURSES_BASIC_INFO,
  CourseBasic,
  QueryCoursesArgs,
  TDataCoursesBasicInfo,
} from 'graphqlAPI'

interface IProps {
  item: IFolderModel
  create: boolean
}

interface IValues {
  value: CourseBasic
  label: string
}

const Select: React.FC<IProps> = ({ item }) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const [search, setSearchBase] = useState('')
  const timeOutRef = useRef<number>()

  const setSearch = useCallback(
    (value: string) => {
      clearTimeout(timeOutRef.current)
      timeOutRef.current = window.setTimeout(() => {
        setSearchBase(value)
      }, 1000)
    },
    [setSearchBase]
  )

  useEffect(() => {
    return () => {
      // This prevent update state when component is unmounted
      clearTimeout(timeOutRef.current)
    }
  }, [])

  const { data: dataCourses, loading } = useQuery<
    TDataCoursesBasicInfo,
    QueryCoursesArgs
  >(COURSES_BASIC_INFO, {
    variables: {
      q: {
        search_name_cont: search,
      },
    },
  })

  const courses = useMemo(() => dataCourses?.courses.nodes || [], [dataCourses])

  const [selected, setSelected] = useState<IValues | null>(null)
  const coursesData = item.courses?.data

  const options = useMemo(() => {
    const coursesIds = new Set(
      (coursesData || [])?.map((course) => course.attributes?.id)
    )
    return courses
      .map((course): IValues => ({ value: course, label: course.name || '-' }))
      .filter((item) => !coursesIds.has(+(item.value.id || 0)))
  }, [courses, coursesData])

  const onSelectedCourseClick = (selectedItem: IValues) => {
    const item_id = item.id?.toString() || '-1'
    localStorage.setItem('openFolder', item_id)

    setSelected(selectedItem)
    dispatch({
      type: fromActions.ASSIGN_COURSE,
      payload: {
        idFolder: item.id,
        idCourse: selectedItem.value.id,
        onSuccess: () => {
          setSelected(null)
        },
      },
    })
  }

  return (
    <SelectInput
      name="state"
      placeHolder={t('administration.folders.folder.search.placeHolder')}
      options={options}
      closeMenuOnSelect={true}
      isClearable
      value={selected}
      isMulti={false}
      withColors={false}
      onChange={(selected) => selected && onSelectedCourseClick(selected)}
      onInputChange={(newValue) => {
        setSearch(newValue)
      }}
      isLoading={!loading}
      loadingMessage={() => t('administration.folders.options.loading')}
    />
  )
}

export default Select
